import { debounce, isEqual, throttle } from 'lodash-es';
import { PdfViewerEventType, PdfViewerDeselectPageEvent, PdfViewerNavigateToPageEvent, PdfViewerSelectPageEvent } from '../../+types/pdf-viewer-event.type';
import { PdfThumbnailViewerPageComponent } from './pdf-thumbnail-viewer-page.component';
export class PdfThumbnailViewer {
    constructor(eventBus, pdfThumbnailViewerConfig, pdfJsService) {
        this.eventBus = eventBus;
        this.pdfThumbnailViewerConfig = pdfThumbnailViewerConfig;
        this.pdfJsService = pdfJsService;
        this.selectedPageIds = new Set();
        this.defaultPageDimensions = { width: 600, height: 600 };
        this.maxPreviewDimensions = { width: 160, height: 100 };
        this.pageComponentsByPageId = new Map();
        this.isOn = false;
        this.onFocusChangeEvent = (event) => {
            this.goto(event.payload.pdfViewerPage.id);
        };
        this.onInitializeSearchEvent = () => {
            this.setHighlightedPages([]);
        };
        this.onPdfViewerFetchSearchResultsEvent = (event) => {
            this.setHighlightedPages(event.payload.pages);
        };
        this.onScroll = (event) => {
            this.containerScrollTop = event.target.scrollTop;
            this.updateViewport();
            this.updatePageIdsInViewport();
            this.debouncedScroll();
        };
        this.onDebouncedScroll = () => {
            this.orchestrateRendering();
        };
        this.onDebouncedResize = () => {
            this.orchestrateRendering();
        };
        this.onCheckboxClicked = (event) => {
            const checkbox = event.currentTarget;
            const thumbnail = checkbox.parentElement.parentElement;
            const pageId = thumbnail.dataset.pageId;
            const checked = checkbox.checked;
            if (checked) {
                this.selectPage(pageId);
            }
            else {
                this.deselectPage(pageId);
            }
        };
        this.onThumbnailClicked = (event) => {
            const pageId = event.currentTarget.dataset.pageId;
            this.eventBus.dispatch(new PdfViewerNavigateToPageEvent({ pageId }));
        };
        this.orchestrateRendering = () => {
            const pageIds = this.determinePageIdsToRender();
            if (!pageIds.length) {
                return;
            }
            pageIds.forEach(pageId => {
                const thumbnailPreviewElement = this.getThumbnailPreviewElement(pageId);
                const thumbnail = this.thumbnailsByPageId.get(pageId);
                thumbnail.dimensions = null;
                const pageComponent = new PdfThumbnailViewerPageComponent(thumbnailPreviewElement, thumbnail.url, this.maxPreviewDimensions, thumbnail.pageNumber, this.pdfJsService);
                this.pageComponentsByPageId.set(pageId, pageComponent);
                pageComponent.render().then(() => {
                    if (!thumbnail.dimensions) {
                        this.resizePreview(pageId, pageComponent.dimensions);
                    }
                });
            });
        };
        this.container = this.pdfThumbnailViewerConfig.thumbnailContainer;
        this.tintColor = this.pdfThumbnailViewerConfig.tintColor;
        this.selectedPageIds = new Set(this.pdfThumbnailViewerConfig.selectedPageIds);
        this.thumbnails = [];
        this.thumbnailsByPageId = new Map();
        this.thumbnailsByPageNumber = new Map();
        this.buildThumbnailsList();
        this.thumbnailHeight = 56 /* LABEL_HEIGHT */ + 96 /* THUMBNAIL_MAX_HEIGHT */;
        this.container.classList.add("sp-pdf-thumbnail-viewer" /* THUMBNAIL_VIEWER */);
        this.container.setAttribute('data-pdf-thumbnail-viewer-id', `${this.pdfThumbnailViewerConfig.id}`);
        this.throttledScroll = throttle(this.onScroll, 50);
        this.debouncedScroll = debounce(this.onDebouncedScroll, 100);
        this.debouncedResize = debounce(this.onDebouncedResize, 50);
        this.debouncedOrchestrateRenderingOnNextTick = debounce(this.orchestrateRendering, 0);
        this.addSearchEventListeners();
    }
    on(pageId) {
        if (this.isOn) {
            return;
        }
        this.isOn = true;
        this.viewport = null;
        this.containerScrollTop = null;
        this.pageIdFocused = pageId;
        this._pageIdsInViewport = [];
        this._pageIdsEnteredViewport = [];
        this._pageIdsLeftViewport = [];
        this.container.innerHTML = this.renderThumbnails(this.thumbnails, pageId);
        this.removeEventListeners();
        this.addEventListeners();
        this.scrollToActiveThumbnail(pageId);
        this.orchestrateRendering();
    }
    off() {
        if (!this.isOn) {
            return;
        }
        this.isOn = false;
        this.removeEventListeners();
        this.destroyPageComponents();
        this.resetContainerElement();
    }
    resize() {
        if (!this.isOn) {
            return;
        }
        this.updateViewport();
        this.updatePageIdsInViewport();
        this.debouncedResize();
    }
    enablePageSelection() {
        this.container.classList.add("enable-page-selection" /* PAGE_SELECTION */);
    }
    disablePageSelection() {
        this.container.classList.remove("enable-page-selection" /* PAGE_SELECTION */);
    }
    selectAllPages() {
        this.thumbnails.forEach(thumbnail => {
            this.selectPage(thumbnail.pageId);
        });
    }
    deselectAllPages() {
        Array.from(this.selectedPageIds.keys()).forEach(pageId => {
            this.deselectPage(pageId);
        });
    }
    destroy() {
        this.thumbnails = [];
        this.removeSearchEventListeners();
        this.removeEventListeners();
        this.destroyPageComponents();
        this.throttledScroll.cancel();
        this.debouncedScroll.cancel();
        this.debouncedResize.cancel();
        this.debouncedOrchestrateRenderingOnNextTick.cancel();
        this.resetContainerElement();
    }
    setHighlightedPages(pageNumbers) {
        this.thumbnails.forEach(thumb => (thumb.isHighlighted = pageNumbers.includes(thumb.pageNumber)));
        this.container.querySelectorAll(`.${"sp-pdf-viewer-thumbnail-container" /* THUMBNAIL_CONTAINER */}.highlight`).forEach(element => {
            element.classList.remove('highlight');
        });
        pageNumbers.forEach((pageNumber) => {
            var _a;
            (_a = this.container.querySelector(`.${"sp-pdf-viewer-thumbnail-container" /* THUMBNAIL_CONTAINER */}[data-page-number="${pageNumber}"]`)) === null || _a === void 0 ? void 0 : _a.classList.add('highlight');
        });
    }
    goto(pageId) {
        if (this.pageIdFocused === pageId) {
            return;
        }
        const oldFocusedThumbnail = this.getThumbnailContainerElement(this.pageIdFocused);
        const focusedThumbnail = this.getThumbnailContainerElement(pageId);
        this.pageIdFocused = pageId;
        if (oldFocusedThumbnail) {
            oldFocusedThumbnail.classList.remove("thumbnail-active" /* THUMBNAIL_ACTIVE */);
        }
        if (focusedThumbnail) {
            focusedThumbnail.classList.add("thumbnail-active" /* THUMBNAIL_ACTIVE */);
        }
        this.scrollToActiveThumbnail(pageId);
    }
    resetContainerElement() {
        if (this.container.getAttribute('data-pdf-thumbnail-viewer-id') === this.pdfThumbnailViewerConfig.id) {
            this.container.innerHTML = '';
        }
    }
    buildThumbnailsList() {
        this.pdfThumbnailViewerConfig.pages.forEach(({ id: pageId, previewUrl, dimensions, pageNumber, annotations }) => {
            let isPartOfRange = false;
            let isFirstPageOfRange = false;
            this.pdfThumbnailViewerConfig.lockedPages &&
                this.pdfThumbnailViewerConfig.lockedPages.forEach(range => {
                    const [start, end] = range.split('-').map(value => Number(value));
                    if (pageNumber >= start && pageNumber <= end) {
                        isPartOfRange = true;
                        isFirstPageOfRange = pageNumber === start;
                    }
                });
            const thumbnail = {
                pageId,
                pageNumber,
                url: previewUrl,
                isPartOfRange,
                isFirstPageOfRange,
                annotations,
                dimensions: dimensions,
                isHighlighted: false
            };
            this.thumbnails.push(thumbnail);
            this.thumbnailsByPageId.set(pageId, thumbnail);
            this.thumbnailsByPageNumber.set(pageNumber, thumbnail);
        });
    }
    renderThumbnails(thumbnails, focusedPageId) {
        return `
        <div class="group-container">${thumbnails
            .map(thumbnail => {
            const { pageId, pageNumber, isPartOfRange, isFirstPageOfRange, isHighlighted } = thumbnail;
            const containerHeight = 152; // limitedHeight + DIMENSIONS.LABEL_HEIGHT;
            const indentClass = isPartOfRange && !isFirstPageOfRange ? "indent-on-selection" /* INDENT */ : '';
            const shouldRenderCheckbox = !isPartOfRange || isFirstPageOfRange;
            const activeClass = focusedPageId === pageId ? "thumbnail-active" /* THUMBNAIL_ACTIVE */ : '';
            const highlightClass = isHighlighted ? "highlight" /* HIGHLIGHT */ : '';
            const checked = this.selectedPageIds.has(pageId);
            const dimensions = thumbnail.dimensions || this.defaultPageDimensions;
            const scaledDimensions = this.scaleDimensions(dimensions, this.maxPreviewDimensions);
            return `
                    <div class="${"sp-pdf-viewer-thumbnail-container" /* THUMBNAIL_CONTAINER */} ${activeClass} ${indentClass} ${highlightClass}" style="height: ${containerHeight}px" data-page-number="${pageNumber}" data-page-id="${pageId}">
                        ${shouldRenderCheckbox ? this.renderCheckbox(checked) : ''}
                        <div class="thumbnail-label" title="${pageNumber}">${pageNumber}</div>
                        <div class="preview-wrapper">
                            <div class="preview" style="width: ${scaledDimensions.width}px; height: ${scaledDimensions.height}px;" ></div>
                        </div>
                    </div>`;
        })
            .join('\n')}
        </div>`;
    }
    scaleDimensions(dimensions, maxDimensions, fill = false) {
        const scaleWidth = maxDimensions.width / dimensions.width;
        const scaleHeight = maxDimensions.height / dimensions.height;
        const scale = fill ? (scaleWidth > scaleHeight ? scaleWidth : scaleHeight) : scaleWidth < scaleHeight ? scaleWidth : scaleHeight;
        return { width: dimensions.width * scale, height: dimensions.height * scale };
    }
    renderCheckbox(checked) {
        return `
            <label class="sp-pdf-thumbnail-viewer-checkbox">
                <input type="checkbox" ${checked ? 'checked' : ''} />
                <div class="sp-pdf-thumbnail-viewer-checkbox__indicator" style="background: ${this.tintColor}"></div>
            </label>
        `;
    }
    addSearchEventListeners() {
        this.eventBus.on(PdfViewerEventType.InitializeSearch, this.onInitializeSearchEvent);
        this.eventBus.on(PdfViewerEventType.FetchSearchResults, this.onPdfViewerFetchSearchResultsEvent);
    }
    removeSearchEventListeners() {
        this.eventBus.off(PdfViewerEventType.InitializeSearch, this.onInitializeSearchEvent);
        this.eventBus.off(PdfViewerEventType.FetchSearchResults, this.onPdfViewerFetchSearchResultsEvent);
    }
    addEventListeners() {
        const thumbnailContainers = this.container.querySelectorAll(`.${"sp-pdf-viewer-thumbnail-container" /* THUMBNAIL_CONTAINER */}`);
        Array.from(thumbnailContainers).forEach(thumbnailContainer => {
            const checkbox = thumbnailContainer.querySelector('input[type="checkbox"]');
            if (checkbox) {
                checkbox.addEventListener('click', this.onCheckboxClicked);
            }
            thumbnailContainer.addEventListener('click', this.onThumbnailClicked);
        });
        this.container.addEventListener('scroll', this.throttledScroll);
        this.eventBus.on(PdfViewerEventType.FocusChange, this.onFocusChangeEvent);
    }
    removeEventListeners() {
        const thumbnailContainers = this.container.querySelectorAll(`.${"sp-pdf-viewer-thumbnail-container" /* THUMBNAIL_CONTAINER */}`);
        Array.from(thumbnailContainers).forEach(thumbnailContainer => {
            const checkbox = thumbnailContainer.querySelector('input[type="checkbox"]');
            if (checkbox) {
                checkbox.removeEventListener('click', this.onCheckboxClicked);
            }
            thumbnailContainer.removeEventListener('click', this.onThumbnailClicked);
        });
        this.container.removeEventListener('scroll', this.throttledScroll);
        this.eventBus.off(PdfViewerEventType.FocusChange, this.onFocusChangeEvent);
    }
    updateViewport() {
        this.viewport = {
            y1: this.containerScrollTop,
            y2: this.container.clientHeight + this.containerScrollTop
        };
    }
    updatePageIdsInViewport() {
        const pageIdsInViewport = [];
        let y = 0;
        this.thumbnails.forEach((thumbnail) => {
            const thumbnailY1 = y;
            const thumbnailY2 = y + this.thumbnailHeight;
            if (this.viewport.y1 <= thumbnailY2 && this.viewport.y2 >= thumbnailY1) {
                pageIdsInViewport.push(thumbnail.pageId);
            }
            y += this.thumbnailHeight;
        });
        if (!isEqual(this.pageIdsInViewport, pageIdsInViewport)) {
            const pageIdsEnteredViewport = pageIdsInViewport.filter(id => !this.pageIdsInViewport.includes(id));
            const pageIdsLeftViewport = this.pageIdsInViewport.filter(id => !pageIdsInViewport.includes(id));
            this.pageIdsInViewport = pageIdsInViewport;
            if (pageIdsEnteredViewport.length) {
                this.pageIdsEnteredViewport = pageIdsEnteredViewport;
            }
            if (pageIdsLeftViewport.length) {
                this.pageIdsLeftViewport = pageIdsLeftViewport;
            }
        }
    }
    resizePreview(pageId, dimensions) {
        const previewElement = this.getThumbnailPreviewElement(pageId);
        const scaledDimensions = this.scaleDimensions(dimensions, this.maxPreviewDimensions);
        previewElement.style.width = `${scaledDimensions.width}px`;
        previewElement.style.height = `${scaledDimensions.height}px`;
    }
    selectPage(pageId) {
        const thumbnail = this.thumbnailsByPageId.get(pageId);
        if (!this.selectedPageIds.has(pageId) && !(thumbnail.isPartOfRange && !thumbnail.isFirstPageOfRange)) {
            this.eventBus.dispatch(new PdfViewerSelectPageEvent({ pageId }));
            this.selectedPageIds.add(pageId);
            const checkbox = this.getCheckboxElement(pageId);
            if (checkbox) {
                checkbox.checked = true;
            }
            if (thumbnail.isFirstPageOfRange) {
                let nextThumbnail = this.thumbnailsByPageNumber.get(thumbnail.pageNumber + 1);
                while (nextThumbnail &&
                    !this.selectedPageIds.has(nextThumbnail.pageId) &&
                    nextThumbnail.isPartOfRange &&
                    !nextThumbnail.isFirstPageOfRange) {
                    this.eventBus.dispatch(new PdfViewerSelectPageEvent({ pageId: nextThumbnail.pageId }));
                    this.selectedPageIds.add(nextThumbnail.pageId);
                    nextThumbnail = this.thumbnailsByPageNumber.get(nextThumbnail.pageNumber + 1);
                }
            }
        }
    }
    deselectPage(pageId) {
        const thumbnail = this.thumbnailsByPageId.get(pageId);
        if (this.selectedPageIds.has(pageId) && !(thumbnail.isPartOfRange && !thumbnail.isFirstPageOfRange)) {
            this.eventBus.dispatch(new PdfViewerDeselectPageEvent({ pageId }));
            this.selectedPageIds.delete(pageId);
            const checkbox = this.getCheckboxElement(pageId);
            if (checkbox) {
                checkbox.checked = false;
            }
            if (thumbnail.isFirstPageOfRange) {
                let nextThumbnail = this.thumbnailsByPageNumber.get(thumbnail.pageNumber + 1);
                while (nextThumbnail &&
                    this.selectedPageIds.has(nextThumbnail.pageId) &&
                    nextThumbnail.isPartOfRange &&
                    !nextThumbnail.isFirstPageOfRange) {
                    this.eventBus.dispatch(new PdfViewerDeselectPageEvent({ pageId: nextThumbnail.pageId }));
                    this.selectedPageIds.delete(nextThumbnail.pageId);
                    nextThumbnail = this.thumbnailsByPageNumber.get(nextThumbnail.pageNumber + 1);
                }
            }
        }
    }
    scrollToActiveThumbnail(pageId) {
        const thumbnailElement = this.getThumbnailContainerElement(pageId);
        if (!thumbnailElement)
            return;
        const thumbnailTop = thumbnailElement.getBoundingClientRect().top - this.container.getBoundingClientRect().top;
        const thumbnailHeight = thumbnailElement.clientHeight;
        const containerHeight = this.container.clientHeight;
        const thumbnailIsHeigherThanViewport = thumbnailTop < 0;
        const thumbnailIsLowerThanViewport = thumbnailTop + thumbnailHeight > containerHeight;
        if (thumbnailIsLowerThanViewport) {
            this.container.scrollTop += thumbnailTop + thumbnailHeight - containerHeight;
        }
        if (thumbnailIsHeigherThanViewport) {
            this.container.scrollTop += thumbnailTop;
        }
        this.updateViewport();
        this.updatePageIdsInViewport();
    }
    get pageIdsInViewport() {
        return this._pageIdsInViewport;
    }
    set pageIdsInViewport(value) {
        this._pageIdsInViewport = value;
    }
    get pageIdsEnteredViewport() {
        return this._pageIdsEnteredViewport;
    }
    set pageIdsEnteredViewport(ids) {
        this._pageIdsEnteredViewport = ids;
    }
    get pageIdsLeftViewport() {
        return this._pageIdsLeftViewport;
    }
    set pageIdsLeftViewport(ids) {
        this._pageIdsLeftViewport = ids;
        ids.forEach(id => {
            let pageComponent;
            if ((pageComponent = this.pageComponentsByPageId.get(id))) {
                pageComponent.destroy();
                this.pageComponentsByPageId.delete(id);
            }
        });
    }
    getThumbnailContainerElement(pageId) {
        return this.container.querySelector(`[data-page-id="${pageId}"]`);
    }
    getThumbnailPreviewElement(pageId) {
        return this.container.querySelector(`[data-page-id="${pageId}"] .preview`);
    }
    getCheckboxElement(pageId) {
        return this.container.querySelector(`[data-page-id="${pageId}"] input[type=checkbox]`);
    }
    destroyPageComponents() {
        this.pageComponentsByPageId.forEach((pageComponent) => pageComponent.destroy());
        this.pageComponentsByPageId = new Map();
    }
    determinePageIdsToRender() {
        return this.pageIdsInViewport.filter(pageId => !this.pageComponentsByPageId.has(pageId));
    }
}
//# sourceMappingURL=pdf-thumbnail-viewer.component.js.map